import { ICommonSettings } from "@kuika/kuika-cl-model";
import { IGoogleRecaptchaMobileSettings } from "@kuika/kuika-cl-model/src/models/common-config";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { ReactNode } from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IGoogleRecaptchaIosSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: IGoogleRecaptchaMobileSettings;
}

export interface IGoogleRecaptchaIosSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingGoogleRecaptchaIosSettingsId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class GoogleRecaptchaIosSettings extends React.PureComponent<
  IGoogleRecaptchaIosSettingsProps,
  IGoogleRecaptchaIosSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IGoogleRecaptchaIosSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingConfig: undefined
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IGoogleRecaptchaIosSettingsProps & IWithSDProps) {
    if (prevProps.editingGoogleRecaptchaIosSettingsId !== this.props.editingGoogleRecaptchaIosSettingsId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (this.props.editingGoogleRecaptchaIosSettingsId) {
      const setting = this.props.commonSettings.googleRecaptchaIosSettings?.find(
        (x) => x.id === this.props.editingGoogleRecaptchaIosSettingsId && !x.isGlobalCommonConfigOnProjectConfig
      );
      this.setState({ editingConfig: setting }, () => {
        if (setting) {
          const fieldsValue = this.formRef.current?.getFieldsValue();

          this.formRef.current?.resetFields();

          fieldsValue.name = setting.name;
          fieldsValue.siteKey = setting.siteKey;
          this.formRef.current?.setFieldsValue(fieldsValue);
        } else {
          this.formRef.current?.resetFields();
        }
      });
    } else {
      this.formRef.current?.resetFields();
    }
  };

  existingNameValidator = async (_rule, value) => {
    const setting = this.props.commonSettings?.googleRecaptchaIosSettings;
    if (!setting) return;

    const isSettingAlreadyExist = setting.some(
      (x) =>
        x.name === value &&
        x.id !== this.props.editingGoogleRecaptchaIosSettingsId &&
        !x.isGlobalCommonConfigOnProjectConfig
    );

    if (isSettingAlreadyExist) throw new Error("Google Recaptcha Setting name is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: IGoogleRecaptchaMobileSettings;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      siteKey: data.siteKey?.trim()
    };
    if (this.props.editingGoogleRecaptchaIosSettingsId) {
      obj.id = this.props.editingGoogleRecaptchaIosSettingsId;

      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "googleRecaptchaIosSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "googleRecaptchaIosSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingGoogleRecaptchaIosSettingsId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingGoogleRecaptchaIosSettingsId || "",
      configKey: "googleRecaptchaIosSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Google Recaptcha setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Google Recaptcha connect API setting"
        secondHeader="Google Recaptcha connect API setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingGoogleRecaptchaIosSettingsId ? this.deleteSetting : undefined}
            deleteTestId="iosGoogleRecaptchaConfigurationDeleteButton"
            submitTestId={`iosGoogleRecaptchaConfiguration${
              this.props.editingGoogleRecaptchaIosSettingsId ? "Update" : "Create"
            }Button`}
            submitLabel={this.props.editingGoogleRecaptchaIosSettingsId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configGoogleIosRecaptchaForm"
          />
        }
      >
        <Form
          id="configGoogleIosRecaptchaForm"
          name="googleRecaptchaIosSettings"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription("iosconfig_googlerecaptchasettingpop_up_description")}
          >
            <div>
              <ConfigField label="Name" required tooltipContentKey="iosconfig_googlerecaptcha_name">
                <Form.Item
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: "Please enter a Google Recaptcha Setting Name."
                    },
                    {
                      pattern: /^[A-Za-z][A-Za-z0-9 ]+$/,
                      message: "Google Recaptcha Setting Name is invalid"
                    },
                    { validator: this.existingNameValidator }
                  ]}
                >
                  <KuikaInput size="md" data-testid="iosGoogleRecaptchaConfigurationNameInput" autoFocus />
                </Form.Item>
              </ConfigField>

              <ConfigField label="Site Key" required tooltipContentKey="iosconfig_googlerecaptcha_sitekey">
                <Form.Item
                  name="siteKey"
                  rules={[
                    {
                      required: true,
                      message: "Please enter a Google Recaptcha Setting Site Key."
                    }
                  ]}
                >
                  <KuikaInput size="md" data-testid="iosGoogleRecaptchaConfigurationSiteKeyInput" autoFocus />
                </Form.Item>
              </ConfigField>
            </div>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
